import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import SectionTitle from '../sectionTitle';
import CustomButton from '../customButton';
import CustomCheckbox from '../customCheckbox';
import Filter from '../filter';
import Search from '../search';
import SelectField from '../selectField';
import NotificationCard from '../notificationCard';
import Reviewer from './reviewer';
import VirtualizedList from '../virtualizedList';
import { prepareSurveyReviewerStatuses } from '../../../utility/survey';
import {
  isArrayEmpty,
  isItemInList,
  isArraySubset,
} from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing(5),
  },
  button: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish1}`,
    color: primary.bluish1,
    borderRadius: 44,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 14,
    lineHeight: '24px',
    padding: spacing(1, 4),
    maxHeight: 32,
  },
  action: {
    width: 118,
    minWidth: 118,
  },
  filters: {
    borderBottom: `1px dashed ${primary.bluish7}`,
    display: 'flex',
    alignItems: 'center',
    paddingBottom: spacing(3),
  },
  spacingRight: {
    marginRight: spacing(2),
  },
  search: {
    marginLeft: 'auto',
  },
  selectPaper: {
    minWidth: 200,
  },
  reviewers: {
    maxHeight: 513,
  },
  reviewersViewport: {
    maxHeight: 513,
  },
  scrollY: {
    right: -18,
    '& .os-scrollbar-vertical': {
      paddingTop: spacing(4),
      paddingBottom: spacing(4),
    },
  },
}));

const SurveyReviewers = ({
  translations,
  actions,
  reviewers = [],
  canManage,
  hasNextPage,
  isNextPageLoading,
  isExpired,
  isScheduled,
  isDisabled,
  isPartOfCycle,
  isNpsSurvey,
  canAddReviewer,
  completionStatus,
  selectedUsers,
  canAccessUserProfileCheck,
  onAddReviewers,
  onGoToPersonProfile,
  onStatusChange,
  onSearch,
  onLoadMore,
  onActionSelect,
  onSelectAll,
  onUserSelect,
}) => {
  const classes = useStyles();
  const hasReviewers = !isArrayEmpty(reviewers);

  const completionStatuses = useMemo(
    () =>
      prepareSurveyReviewerStatuses(
        translations.completionStatuses,
        true,
        isNpsSurvey
      ),
    [translations.completionStatuses, isNpsSurvey]
  );

  const getVirtualReviewersize = useCallback(() => 57, []);

  const renderReviewer = useCallback(
    reviewer => (
      <Reviewer
        reviewer={reviewer}
        isSelected={isItemInList(selectedUsers, reviewer)}
        isClickable={canAccessUserProfileCheck(reviewer)}
        isSelectable={canManage && !isPartOfCycle}
        completionStatuses={completionStatuses}
        isDisabled={isScheduled || isExpired || isDisabled}
        onGoToPersonProfile={onGoToPersonProfile}
        onChange={onUserSelect}
      />
    ),
    [
      selectedUsers,
      completionStatuses,
      canManage,
      isDisabled,
      isExpired,
      isPartOfCycle,
      isScheduled,
      canAccessUserProfileCheck,
      onUserSelect,
      onGoToPersonProfile,
    ]
  );

  return (
    <div>
      <div className={classes.header}>
        <SectionTitle variant="h5" title={translations.title} />
        {canManage && !isPartOfCycle && canAddReviewer && !isDisabled && (
          <CustomButton
            className={classes.button}
            type="addWithTextRounded"
            onClick={onAddReviewers}
          >
            {isScheduled
              ? translations.addScheduleButton
              : translations.addButton}
          </CustomButton>
        )}
      </div>
      <div className={classes.filters}>
        {canManage && !isPartOfCycle && (
          <CustomCheckbox
            className={classes.spacingRight}
            isChecked={
              !isArrayEmpty(selectedUsers) &&
              !isArrayEmpty(reviewers) &&
              isArraySubset(selectedUsers, reviewers)
            }
            onChange={onSelectAll}
            isControlled
            disabled={isScheduled || isDisabled || !hasReviewers}
          />
        )}
        {canManage && !isPartOfCycle && (
          <SelectField
            className={classNames(classes.action, classes.spacingRight)}
            menuClass={classes.selectPaper}
            placeholder={translations.action}
            options={actions}
            parser={{ label: 'name', value: 'id' }}
            isDisabled={isScheduled || isDisabled || !hasReviewers}
            onChange={onActionSelect}
            isSearchDisabled
            shouldRemoveLabel
          />
        )}
        <Filter
          name={translations.status}
          options={completionStatuses}
          value={completionStatus}
          isDisabled={isScheduled}
          onChange={onStatusChange}
        />
        <Search
          className={classes.search}
          placeholder={translations.search}
          onChange={onSearch}
        />
      </div>
      {hasReviewers ? (
        <div className={classes.reviewers}>
          <VirtualizedList
            viewportClass={classes.reviewersViewport}
            verticalScrollClass={classes.scrollY}
            items={reviewers}
            hasNextPage={hasNextPage}
            isNextPageLoading={isNextPageLoading}
            count={reviewers.length}
            estimateSize={getVirtualReviewersize}
            renderItem={renderReviewer}
            onLoadMore={onLoadMore}
          />
        </div>
      ) : (
        <NotificationCard
          shouldFade={!hasReviewers}
          title={translations.noReviewers}
        />
      )}
    </div>
  );
};

SurveyReviewers.propTypes = {
  translations: PropTypes.object.isRequired,
  actions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reviewers: PropTypes.arrayOf(PropTypes.shape({})),
  completionStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  canManage: PropTypes.bool.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  isNextPageLoading: PropTypes.bool.isRequired,
  isScheduled: PropTypes.bool.isRequired,
  isExpired: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  canAddReviewer: PropTypes.bool.isRequired,
  selectedUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  isPartOfCycle: PropTypes.bool.isRequired,
  isNpsSurvey: PropTypes.bool.isRequired,
  onAddReviewers: PropTypes.func.isRequired,
  onActionSelect: PropTypes.func.isRequired,
  onGoToPersonProfile: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onUserSelect: PropTypes.func.isRequired,
};

export default SurveyReviewers;
